import { type FormEvent, useState } from 'react';
import { AlertTriangle, Check, Leaf, Loader2, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import './site-chrome.css';

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: 'include', ...init });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || body.message || 'Request failed');
  return body as T;
}

export function SiteFooter() {
  return (
    <footer className="production-footer">
      <div><Leaf size={17} /><strong>PlantGrow</strong><span>Plant identification can be wrong. Never eat or medicate with a plant based only on an AI result.</span></div>
      <nav>
        <Link to="/plants">Plant library</Link>
        <Link to="/pricing">Pricing</Link>
        <Link to="/account">Account</Link>
        <Link to="/privacy">Privacy</Link>
        <Link to="/terms">Terms</Link>
        <Link to="/community-guidelines">Community guidelines</Link>
      </nav>
    </footer>
  );
}

export function PlantSafetyNoticeLite() {
  return (
    <aside className="plant-safety-notice">
      <AlertTriangle size={18} />
      <p><strong>Identification safety:</strong> PlantGrow can make mistakes. Do not eat, medicate with, or handle an unfamiliar plant solely because of an AI identification. Verify high-risk plants with a qualified local expert.</p>
    </aside>
  );
}

export function PlantCorrectionWidgetLite() {
  const location = useLocation();
  const match = location.pathname.match(/^\/plants\/([^/]+)\/?$/);
  const plantId = match?.[1] ? decodeURIComponent(match[1]) : null;
  const [open, setOpen] = useState(false);
  const [suggestedName, setSuggestedName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  if (!plantId) return null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setMessage('');
    try {
      await api('/api/community/manage/corrections', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ plantId, suggestedName, note }),
      });
      setMessage('Thanks — this identification has been sent for review.');
      setSuggestedName('');
      setNote('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not send correction');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" className="wrong-id-fab" onClick={() => setOpen(true)}><AlertTriangle size={16} /> Wrong plant?</button>
      {open && (
        <div className="production-modal-backdrop" onClick={() => setOpen(false)}>
          <section className="production-modal" role="dialog" aria-modal="true" aria-label="Suggest a plant correction" onClick={(event) => event.stopPropagation()}>
            <button className="production-modal-close" type="button" onClick={() => setOpen(false)} aria-label="Close"><X size={20} /></button>
            <span className="production-kicker">Improve identification</span>
            <h2>Think this is the wrong plant?</h2>
            <p>Your correction becomes part of the accuracy review queue. It does not immediately overwrite the shared plant profile.</p>
            <form onSubmit={submit} className="production-form">
              <label>What plant do you think it is?<input value={suggestedName} onChange={(event) => setSuggestedName(event.target.value)} maxLength={120} placeholder="Common or scientific name" /></label>
              <label>What looks wrong?<textarea value={note} onChange={(event) => setNote(event.target.value)} rows={4} maxLength={600} placeholder="Leaf shape, flower, growth habit, location, or another clue" /></label>
              {message && <div className="production-inline-message">{message}</div>}
              <button className="production-primary" disabled={busy || (!suggestedName.trim() && !note.trim())}>{busy ? <Loader2 className="spin" size={17} /> : <Check size={17} />} Send for review</button>
            </form>
          </section>
        </div>
      )}
    </>
  );
}
